<script lang="ts">
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import type { INotification } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { getContext, onDestroy, onMount } from 'svelte'
    import { fade } from 'svelte/transition'
    import type { Writable } from 'svelte/store'
    import Icon from '@iconify/svelte'
    import { cardHeaderStyle } from 'hydria/context'

    interface Props {
        readOnly: boolean
        tokenExpired: () => void
        saved: () => void
        close: () => void
    }

    let { readOnly, tokenExpired, saved, close }: Props = $props()

    let loading = $state(true)
    let saving = $state(false)
    let codes: string[] = $state([])

    let downloadUrl: string = $state('')

    let stoken = getContext('securityToken') as Writable<string>

    async function load() {
        loading = true

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', '/json/security/1/twofa-recovery-codes', undefined, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            notify({
                type: 'danger',
                title: $_('Sécurité'),
                text: $_('Security data could not be loaded. Please try again.'),
            })
            console.error('Failed to load security data:', r)
            return
        }

        codes = r.data.recovery_codes.map(cleanCode)

        loading = false
    }

    function cleanCode(code: string) {
        return code.substring(0, 5) + '-' + code.substring(5, 10)
    }

    function makeDownloadUrl(codes: string[]): string {
        let content = codes.join('\n') + '\n'
        const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' })
        return window.URL.createObjectURL(blob)
    }

    $effect(() => {
        downloadUrl = makeDownloadUrl(codes)
    })

    async function generateCodes() {
        if (loading || saving) return

        saving = true

        const notifyError: INotification = {
            type: 'danger',
            title: $_('Sécurité'),
            text: $_('Des codes n’ont pas pu être générés.'),
        }
        let r: any
        try {
            r = await simpleFetch('POST', '/json/security/1/twofa-recovery-codes', {}, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
            console.error('Failed to generate codes:', e)
        }
        if (r && r.data && r.data.success) {
            let m: string
            if (codes.length) {
                m = $_('De nouveaux codes ont été générés. Les anciens codes ne sont plus valides.')
            } else {
                m = $_('Des codes de récupération ont été générés.')
            }
            notify({
                type: 'success',
                title: $_('Sécurité'),
                text: m,
            })
            saved()
            codes = r.data.recovery_codes.map(cleanCode)
        } else {
            notify(notifyError)
        }
        saving = false
    }

    function copyToClipboard() {
        navigator.clipboard.writeText(codes.join('\n')).then(
            () => {
                notify({
                    type: 'success',
                    title: $_('Sécurité'),
                    text: $_('Les codes ont été copiés.'),
                })
            },
            () => {},
        )
    }

    onMount(() => {
        load()
    })

    // If *we* have set modal-open on the document
    let addedModalOpen = false
    if (!document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.add('modal-open')
        addedModalOpen = true
    }

    onDestroy(() => {
        if (addedModalOpen && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    li {
        font-family: var(--bs-font-monospace);
        font-size: 1.1em;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (!saving) {
                close()
            }
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="card">
                <div class={cardHeaderStyle}>{$_('Codes de récupération')}</div>
                <div class="card-body">
                    <p>{$_('Les codes de récupération permettent d’accéder à votre compte, et la section de sécurité, au cas où il devient impossible de se connecter à l’aide de vos méthodes d’authentification à deux facteurs.')}</p>
                </div>
                {#if loading || saving}
                    <div class="card-body">
                        <div class="m-4 d-flex justify-content-center align-items-center">
                            <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
                        </div>
                    </div>
                {:else if codes.length > 0}
                    <div class="card-body">
                        <p>{$_('Conservez ces codes aussi secrètement que votre mot de passe. Nous recommendons l’utilisation d’un gestionnaire de mots de passe.')}</p>

                        <p>{$_('Un code ne peut être utilisé qu’une seule fois.')}</p>

                        <p>{$_('Les codes actifs :')}</p>

                        <ul>
                            {#each codes as code}
                                <li>{code}</li>
                            {/each}
                        </ul>
                        <div class="code-actions">
                            <button
                                type="button"
                                class="btn btn-secondary"
                                onclick={() => {
                                    copyToClipboard()
                                }}><Icon inline icon="mdi:content-copy" /> {$_('Copier')}</button
                            >
                            <a href={downloadUrl} download="codes-webdomaine.txt" class="btn btn-secondary"><Icon inline icon="mdi:download" /> {$_('Télécharger')}</a>
                        </div>
                    </div>
                {/if}
                <div class="card-footer text-end">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        disabled={saving}
                        onclick={() => {
                            close()
                        }}>{$_('Close')}</button
                    >
                    {#if !readOnly}
                        <button
                            type="button"
                            class="btn btn-outline-primary"
                            disabled={saving}
                            onclick={() => {
                                generateCodes()
                            }}
                        >
                            {#if codes.length}
                                {$_('Générer de nouveaux codes')}
                            {:else}
                                {$_('Générer des codes')}
                            {/if}
                        </button>
                    {/if}
                </div>
            </div>
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            if (!saving) close()
        }}
        aria-hidden="true"
    ></div>
</div>
